/* RESPONSIBLE TEAM: team-reporting */

import { ChartDefinition, ChartSchema } from '@/features/report-snapshots/api/schema';
import { t } from '@lingui/core/macro';
import { isHeatmapChart } from '@/features/report-snapshots/utils/common';
import {
  AggregationNameResolver,
  defaultAggregationNameResolver,
} from '@/features/report-snapshots/resolvers/aggregation';
import { PropertyNameResolver } from '@/features/report-snapshots/resolvers/property';
import { ChartType } from '@/features/report-snapshots/types';

function buildChartTitle(
  chartDefinition: ChartDefinition,
  opts?: {
    resolvePropertyName?: PropertyNameResolver;
    resolveAggregationName?: AggregationNameResolver;
  },
) {
  const resolveAggregationName = opts?.resolveAggregationName ?? defaultAggregationNameResolver;

  if (chartDefinition.title) {
    return chartDefinition.title;
  }

  const primaryMetricCustomLabel = chartDefinition.chartSeries[0]?.label;
  const primaryMetricDisplayName = chartDefinition.chartSeries[0]?.metricDisplayName;
  const primaryAggregation = chartDefinition.chartSeries[0]?.aggregation;

  let title = primaryMetricCustomLabel ?? primaryMetricDisplayName;

  if (primaryAggregation && resolveAggregationName(primaryAggregation)) {
    title = `${resolveAggregationName(primaryAggregation)} ${title}`;
  }

  if (chartDefinition.chartSeries.length > 1) {
    const numberOfMetrics = chartDefinition.chartSeries.length - 1;
    title = `${title} + ${t`${numberOfMetrics} more`}`;
  }

  if (isHeatmapChart(chartDefinition.visualizationType as ChartType)) {
    const titleText = title ?? chartDefinition.chartSeries[0]?.metricId;
    return t`Heatmap of ${titleText}`;
  }

  const { viewBy, viewByDisplay, segmentBy, segmentByDisplay } = chartDefinition;
  if (viewBy) title = `${title} ${t`by`} ${viewByDisplay}`;
  if (segmentBy) title = `${title} ${t`and`} ${segmentByDisplay}`;

  return title;
}

interface ChartTitleProps {
  chartSchema?: ChartSchema;
  chartDefinition: ChartDefinition;
  resolvePropertyName?: PropertyNameResolver;
  resolveAggregationName?: AggregationNameResolver;
}

export function ChartTitle(props: ChartTitleProps) {
  const { chartDefinition, resolvePropertyName, resolveAggregationName } = props;
  const title = buildChartTitle(chartDefinition, { resolvePropertyName, resolveAggregationName });
  const description = chartDefinition.description;

  return (
    <div className="flex flex-col gap-1">
      <div className="text-h3 truncate">{title}</div>
      {description && <div className="text-support text-muted pb-2 truncate">{description}</div>}
    </div>
  );
}
